import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { getDamageType } from "@/utils/damageTypeData";
import { Shield, AlertTriangle, CheckCircle, ArrowRight } from "lucide-react";
import HeroSection from "@/components/layout/HeroSection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getOrganizationSchema, getBreadcrumbSchema, getFAQPageSchema } from "@/utils/seoSchemas";
import StructuredData from "@/components/StructuredData";
import { useImagePreload } from "@/hooks/useImagePreload";
import { motion } from "framer-motion";
import { ScrollAnimatedSection } from "@/components/shared/ScrollAnimatedSection";
import { GlowCard } from "@/components/shared/GlowCard";
import { InteractiveCard } from "@/components/ui/interactive-card";
import ContactForm from "@/components/ContactForm";
import { Link } from "react-router-dom";
import ransomwareHero from "@/assets/damage-types/ransomware-hero.jpg";
import datenverlustHero from "@/assets/damage-types/datenverlust-hero.jpg";
import phishingHero from "@/assets/damage-types/phishing-hero.jpg";
import patientendatenHero from "@/assets/damage-types/patientendaten-diebstahl-hero.jpg";
import betriebsunterbrechungHero from "@/assets/damage-types/betriebsunterbrechung-hero.jpg";
import dsgvoHero from "@/assets/damage-types/dsgvo-hero.jpg";

interface DamageTypePageProps {
  slug: string;
}

const DamageTypePage = ({ slug }: DamageTypePageProps) => {
  const damageType = getDamageType(slug);

  if (!damageType) {
    return <div>Schaden nicht gefunden</div>;
  }

  const damageTypeImages: Record<string, string> = {
    "ransomware-schutz": ransomwareHero,
    "datenverlust": datenverlustHero,
    "phishing-schutz": phishingHero,
    "patientendaten-diebstahl": patientendatenHero,
    "betriebsunterbrechung": betriebsunterbrechungHero,
    "dsgvo-bussgelder": dsgvoHero,
  };

  const heroImage = damageTypeImages[slug];
  
  useImagePreload(heroImage);

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Home", url: "https://praxis-versichert.de" },
    { name: "Cyberversicherung", url: "https://praxis-versichert.de/cyberversicherung" },
    { name: damageType.name }
  ]);

  const faqSchema = getFAQPageSchema(
    damageType.faqs.map(faq => ({
      question: faq.question,
      answer: faq.answer
    }))
  );

  return (
    <>
      <Helmet>
        <title>{damageType.title}</title>
        <meta name="description" content={damageType.metaDescription} />
        <link rel="canonical" href={`https://praxis-versichert.de/cyberversicherung/${damageType.slug}`} />
        
        <meta property="og:title" content={damageType.title} />
        <meta property="og:description" content={damageType.metaDescription} />
        
        <script type="application/ld+json">
          {JSON.stringify(getOrganizationSchema())}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <StructuredData data={[getOrganizationSchema(), breadcrumbSchema, faqSchema]} />

      <Navigation />
      
      <main>
        {/* Hero with Animation */}
        {heroImage && (
          <HeroSection image={heroImage} alt={`${damageType.heroTitle} - Versicherungsschutz und Prävention für medizinische Einrichtungen`} title={damageType.heroTitle} gradientPreset="none" imageOpacity={40} overlayColor="background" className="py-20 sm:py-24 lg:py-32 px-4">
            <ScrollAnimatedSection>
              <div className="container mx-auto max-w-4xl text-center relative z-10">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="inline-flex items-center justify-center w-12 h-12 bg-accent/10 rounded-full mb-6"
                >
                  <Shield className="w-6 h-6 text-accent" />
                </motion.div>
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                >
                  {damageType.heroTitle}
                </motion.h1>
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  className="text-lg text-foreground max-w-2xl mx-auto font-semibold mb-6"
                >
                  {damageType.heroSubtitle}
                </motion.p>
              </div>
            </ScrollAnimatedSection>
          </HeroSection>
        )}
        
        {!heroImage && (
          <section className="py-20 sm:py-24 lg:py-32 px-4 bg-background">
            <ScrollAnimatedSection>
              <div className="container mx-auto max-w-4xl text-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="inline-flex items-center justify-center w-12 h-12 bg-accent/10 rounded-full mb-6"
                >
                  <Shield className="w-6 h-6 text-accent" />
                </motion.div>
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                >
                  {damageType.heroTitle}
                </motion.h1>
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-lg text-foreground max-w-2xl mx-auto font-semibold mb-6"
                >
                  {damageType.heroSubtitle}
                </motion.p>
              </div>
            </ScrollAnimatedSection>
          </section>
        )}

        {/* Stats with GlowCard */}
        <section className="py-12 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/[0.02] to-background" />
          <div className="container mx-auto max-w-6xl px-4 relative z-10">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {damageType.statistics.map((stat, i) => (
                <GlowCard key={i} to="#" index={i}>
                  <div className="text-2xl sm:text-3xl font-bold text-accent mb-2">{stat.value}</div>
                  <div className="text-sm font-semibold mb-1">{stat.label}</div>
                  <div className="text-xs text-muted-foreground">{stat.description}</div>
                </GlowCard>
              ))}
            </div>
          </div>
        </section>

        {/* Description & How it Works with InteractiveCard */}
        <section className="py-16">
          <ScrollAnimatedSection>
            <div className="container mx-auto max-w-4xl px-4">
              <p className="text-lg text-muted-foreground mb-12">{damageType.description}</p>
              
              <h2 className="text-3xl font-bold mb-6">{damageType.howItWorks.title}</h2>
              <p className="text-muted-foreground mb-6">{damageType.howItWorks.description}</p>
              <div className="space-y-4">
                {damageType.howItWorks.steps.map((step, i) => (
                  <InteractiveCard 
                    key={i}
                    enableTilt={true}
                    enableDynamicShadow={true}
                    className="p-4 border-border/50"
                  >
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center text-accent font-bold">{i + 1}</div>
                      <p className="text-sm">
                        <strong className="font-bold">{step.title}:</strong> {step.description}
                      </p>
                    </div>
                  </InteractiveCard>
                ))}
              </div>
            </div>
          </ScrollAnimatedSection>
        </section>

        {/* Real Cases with InteractiveCard */}
        <section className="py-16 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-accent/[0.02] via-background to-accent/[0.02]" />
          <ScrollAnimatedSection>
            <div className="container mx-auto max-w-6xl px-4 relative z-10">
              <h2 className="text-3xl font-bold mb-12 text-center">Echte Schadensbeispiele</h2>
              <div className="grid lg:grid-cols-2 gap-8">
                {damageType.realCases.map((case_, i) => (
                  <InteractiveCard 
                    key={i}
                    className="p-8 hover:shadow-[0_0_30px_rgba(139,195,74,0.2)]"
                    enableTilt={true}
                    enableDynamicShadow={true}
                  >
                    <h3 className="text-xl font-bold mb-2">{case_.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{case_.location}</p>
                    <p className="text-2xl font-bold text-red-500 mb-4">{case_.damage}</p>
                    <p className="mb-6">{case_.description}</p>
                    <ul className="space-y-2">
                      {case_.consequences.map((cons, j) => (
                        <li key={j} className="flex gap-2 text-sm">
                          <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                          <span>{cons}</span>
                        </li>
                      ))}
                    </ul>
                  </InteractiveCard>
                ))}
              </div>
            </div>
          </ScrollAnimatedSection>
        </section>

        {/* Insurance Protection with GlowCard */}
        <section className="py-16">
          <ScrollAnimatedSection>
            <div className="container mx-auto max-w-6xl px-4">
              <h2 className="text-3xl font-bold mb-12 text-center bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                {damageType.insuranceProtection.title}
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {damageType.insuranceProtection.coverages.map((cov, i) => (
                  <GlowCard key={i} to="#" index={i}>
                    <div className="p-6">
                      <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        {cov.name}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3">{cov.description}</p>
                      <div className="p-3 bg-accent/10 rounded text-sm">
                        <strong>Beispiel:</strong> {cov.example}
                      </div>
                    </div>
                  </GlowCard>
                ))}
              </div>
            </div>
          </ScrollAnimatedSection>
        </section>

        {/* Best Tariffs with InteractiveCard */}
        <section className="py-16 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/[0.02] to-background" />
          <ScrollAnimatedSection>
            <div className="container mx-auto max-w-4xl px-4 relative z-10">
              <h2 className="text-3xl font-bold mb-8 text-center">Top-Tarife für {damageType.name}</h2>
              <div className="space-y-4">
                {damageType.bestTariffs.map((tariff, i) => (
                  <InteractiveCard key={i} className="p-6 flex justify-between items-center" enablePulse={true}>
                    <div>
                      <h3 className="font-bold text-lg">{tariff.tariff}</h3>
                      <p className="text-sm text-muted-foreground">{tariff.provider} • {tariff.coverage}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-accent">{tariff.highlight}</div>
                    </div>
                  </InteractiveCard>
                ))}
              </div>
            </div>
          </ScrollAnimatedSection>
        </section>

        {/* Prevention with Animation */}
        <section className="py-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-accent/10" />
          <ScrollAnimatedSection>
            <div className="container mx-auto max-w-4xl px-4 relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-3xl font-bold mb-8">{damageType.prevention.title}</h2>
                {damageType.prevention.measures.map((measure, i) => (
                  <div key={i} className="mb-8">
                    <h3 className="text-xl font-bold mb-4">{measure.category}</h3>
                    <ul className="space-y-2">
                      {measure.actions.map((action, j) => (
                        <li key={j} className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                          <span>{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </motion.div>
            </div>
          </ScrollAnimatedSection>
        </section>

        {/* FAQ with Animation */}
        <section className="py-16 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-accent/[0.02] via-background to-background" />
          <ScrollAnimatedSection>
            <div className="container mx-auto max-w-4xl px-4 relative z-10">
              <h2 className="text-3xl font-bold mb-8 text-center">Häufige Fragen</h2>
              <Accordion type="single" collapsible className="space-y-4">
                {damageType.faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="bg-card border border-border rounded-lg px-6">
                    <AccordionTrigger className="text-left hover:no-underline">
                      <span className="font-semibold">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </ScrollAnimatedSection>
        </section>

        {/* New CTA Section with ContactForm */}
        <section className="py-20 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/15 via-accent/5 to-accent/15" />
          <motion.div 
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent to-transparent"
          />
          
          <div className="container mx-auto max-w-5xl relative z-10">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <ScrollAnimatedSection>
                <h2 className="text-3xl font-bold mb-4">Jetzt persönlich beraten lassen</h2>
                <p className="text-muted-foreground mb-6">
                  Unsere Experten helfen Ihnen, den optimalen Schutz gegen {damageType.name} zu finden.
                </p>
                <Link 
                  to="/termin-buchen"
                  className="inline-flex items-center px-8 py-4 bg-transparent text-white border-2 border-accent rounded-md font-semibold transition-all hover:shadow-[0_0_25px_rgba(139,195,74,0.5)]"
                >
                  Jetzt Termin vereinbaren
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </ScrollAnimatedSection>
              
              <div className="bg-card/80 backdrop-blur-sm border border-accent/30 rounded-xl p-6 hover:border-accent/50 transition-colors">
                <h3 className="text-xl font-semibold mb-4 text-center">Beratung anfragen</h3>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default DamageTypePage;
